// src/hooks/useDocumentSharing.js
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  shareDocument,
  shareDocuments,
  fetchDocumentAccessList,
  revokeDocumentAccess,
  revokeAllAccessForDoctor,
  extendDocumentAccess,
  getDoctorSharedDocuments,
  getPatientDoctorsWithAccess,
  getSharingStatistics,
} from "../redux/thunks/sharingthunks";
import {
  removeAccessEntryLocal,
  removeAllDoctorAccessLocal,
  clearShareError,
  clearRevokeError,
  clearExtendError,
  clearLastShareResult,
} from "../redux/slices/sharingslice";
import {
  selectAccessListByDocument,
  selectActiveAccessListByDocument,
  selectAccessStatusByDocument,
  selectDoctorHasAccess,
  selectActiveAccessCountByDocument,
  selectShareLoading,
  selectShareError,
  selectLastShareResult,
  selectRevokeLoading,
  selectRevokeError,
  selectExtendLoading,
  selectExtendError,
  selectDoctorSharedDocuments,
  selectDoctorSharedDocumentsTotal,
  selectDoctorSharedDocumentsLoading,
  selectPatientDoctorsWithAccess,
  selectPatientDoctorsWithAccessLoading,
  selectSharingStatistics,
  selectSharingStatisticsLoading,
} from "../redux/selectors/sharingselectors";

// ─────────────────────────────────────────────
// Hook principal : gestion complète du partage
// ─────────────────────────────────────────────
export function useDocumentSharing() {
  const dispatch = useDispatch();

  // States globaux
  const shareLoading = useSelector(selectShareLoading);
  const shareError = useSelector(selectShareError);
  const lastShareResult = useSelector(selectLastShareResult);
  const revokeLoading = useSelector(selectRevokeLoading);
  const revokeError = useSelector(selectRevokeError);
  const extendLoading = useSelector(selectExtendLoading);
  const extendError = useSelector(selectExtendError);

  // Actions
  const handleShareDocument = useCallback(
    ({ documentId, doctorIds, expiresInHours = null }) =>
      dispatch(shareDocument({ documentId, doctorIds, expiresInHours })),
    [dispatch]
  );

  const handleShareDocuments = useCallback(
    ({ documents, doctorIds, expiresInHours = null }) =>
      dispatch(shareDocuments({ documents, doctorIds, expiresInHours })),
    [dispatch]
  );

  const handleFetchAccessList = useCallback(
    (documentId) => dispatch(fetchDocumentAccessList({ documentId })),
    [dispatch]
  );

  /**
   * Révoque l'accès d'un ou plusieurs médecins à un document.
   * Effectue aussi une mise à jour optimiste locale.
   */
  const handleRevokeDocumentAccess = useCallback(
    async ({ documentId, doctorIds }) => {
      // Optimiste : mise à jour locale immédiate
      doctorIds.forEach((doctorId) => {
        dispatch(removeAccessEntryLocal({ documentId, doctorId }));
      });
      return dispatch(revokeDocumentAccess({ documentId, doctorIds }));
    },
    [dispatch]
  );

  /**
   * Révoque TOUS les accès d'un médecin (tous documents du patient).
   * Met à jour localement sans attendre la réponse serveur.
   */
  const handleRevokeAllAccessForDoctor = useCallback(
    async ({ patientId, doctorId }) => {
      // Optimiste : suppression locale immédiate
      dispatch(removeAllDoctorAccessLocal({ doctorId }));
      return dispatch(revokeAllAccessForDoctor({ patientId, doctorId }));
    },
    [dispatch]
  );

  const handleExtendAccess = useCallback(
    ({ documentId, doctorId, expiresInHours }) =>
      dispatch(extendDocumentAccess({ documentId, doctorId, expiresInHours })),
    [dispatch]
  );

  const handleGetDoctorSharedDocuments = useCallback(
    ({ skip = 0, limit = 50 } = {}) =>
      dispatch(getDoctorSharedDocuments({ skip, limit })),
    [dispatch]
  );

  const handleGetPatientDoctorsWithAccess = useCallback(
    () => dispatch(getPatientDoctorsWithAccess()),
    [dispatch]
  );

  const handleGetSharingStatistics = useCallback(
    () => dispatch(getSharingStatistics()),
    [dispatch]
  );

  const handleClearShareError = useCallback(
    () => dispatch(clearShareError()),
    [dispatch]
  );

  const handleClearRevokeError = useCallback(
    () => dispatch(clearRevokeError()),
    [dispatch]
  );

  const handleClearExtendError = useCallback(
    () => dispatch(clearExtendError()),
    [dispatch]
  );

  const handleClearLastShareResult = useCallback(
    () => dispatch(clearLastShareResult()),
    [dispatch]
  );

  return {
    // States
    shareLoading,
    shareError,
    lastShareResult,
    revokeLoading,
    revokeError,
    extendLoading,
    extendError,

    // Actions
    shareDocument: handleShareDocument,
    shareDocuments: handleShareDocuments,
    fetchAccessList: handleFetchAccessList,
    revokeDocumentAccess: handleRevokeDocumentAccess,
    revokeAllAccessForDoctor: handleRevokeAllAccessForDoctor,
    extendAccess: handleExtendAccess,
    getDoctorSharedDocuments: handleGetDoctorSharedDocuments,
    getPatientDoctorsWithAccess: handleGetPatientDoctorsWithAccess,
    getSharingStatistics: handleGetSharingStatistics,
    clearShareError: handleClearShareError,
    clearRevokeError: handleClearRevokeError,
    clearExtendError: handleClearExtendError,
    clearLastShareResult: handleClearLastShareResult,
  };
}

// ─────────────────────────────────────────────
// Hook spécialisé : accès pour un document précis
// ─────────────────────────────────────────────
export function useDocumentAccess(documentId) {
  const dispatch = useDispatch();

  const accessList = useSelector(selectAccessListByDocument(documentId));
  const activeAccessList = useSelector(
    selectActiveAccessListByDocument(documentId)
  );
  const { loading, error } = useSelector(
    selectAccessStatusByDocument(documentId)
  );
  const activeCount = useSelector(
    selectActiveAccessCountByDocument(documentId)
  );
  const revokeLoading = useSelector(selectRevokeLoading);

  const fetchAccess = useCallback(() => {
    if (documentId) dispatch(fetchDocumentAccessList({ documentId }));
  }, [dispatch, documentId]);

  /**
   * Révoque l'accès d'un médecin à ce document.
   * Mise à jour optimiste locale + appel API.
   */
  const revokeAccess = useCallback(
    async (doctorId) => {
      if (!documentId || !doctorId) return;
      // Optimiste
      dispatch(removeAccessEntryLocal({ documentId, doctorId }));
      return dispatch(
        revokeDocumentAccess({ documentId, doctorIds: [String(doctorId)] })
      );
    },
    [dispatch, documentId]
  );

  const checkDoctorAccess = useCallback(
    (doctorId) =>
      accessList.some(
        (e) =>
          String(e.doctorId) === String(doctorId) &&
          e.status === "ACTIVE" &&
          !e.isExpired
      ),
    [accessList]
  );

  return {
    accessList,
    activeAccessList,
    loading,
    error,
    activeCount,
    revokeLoading,
    fetchAccess,
    revokeAccess,
    checkDoctorAccess,
  };
}

// ─────────────────────────────────────────────
// Hook spécialisé : côté médecin
// ─────────────────────────────────────────────
export function useDoctorSharedDocuments() {
  const dispatch = useDispatch();

  const documents = useSelector(selectDoctorSharedDocuments);
  const total = useSelector(selectDoctorSharedDocumentsTotal);
  const loading = useSelector(selectDoctorSharedDocumentsLoading);

  const fetch = useCallback(
    ({ skip = 0, limit = 50 } = {}) =>
      dispatch(getDoctorSharedDocuments({ skip, limit })),
    [dispatch]
  );

  return { documents, total, loading, fetch };
}

// ─────────────────────────────────────────────
// Hook spécialisé : médecins avec accès (côté patient)
// ─────────────────────────────────────────────
export function usePatientDoctorsWithAccess() {
  const dispatch = useDispatch();

  const doctors = useSelector(selectPatientDoctorsWithAccess);
  const loading = useSelector(selectPatientDoctorsWithAccessLoading);

  const fetch = useCallback(
    () => dispatch(getPatientDoctorsWithAccess()),
    [dispatch]
  );

  /**
   * Révoque tous les accès d'un médecin (optimiste + API).
   */
  const revokeAllForDoctor = useCallback(
    async ({ patientId, doctorId }) => {
      dispatch(removeAllDoctorAccessLocal({ doctorId }));
      return dispatch(revokeAllAccessForDoctor({ patientId, doctorId }));
    },
    [dispatch]
  );

  return { doctors, loading, fetch, revokeAllForDoctor };
}

// ─────────────────────────────────────────────
// Hook : statistiques de partage
// ─────────────────────────────────────────────
export function useSharingStatistics() {
  const dispatch = useDispatch();
  const statistics = useSelector(selectSharingStatistics);
  const loading = useSelector(selectSharingStatisticsLoading);

  const fetch = useCallback(() => dispatch(getSharingStatistics()), [dispatch]);

  return { statistics, loading, fetch };
}